import React from "react";
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Printer, Mail, MapPin, CreditCard, Package } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const OrderDetails = ({ order, isOpen, onClose, onStatusChange }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  if (!order) return null;

  // Sinds de webshop afhalen/verzenden aanbiedt zet de checkout dit mee in
  // customer_details. Oudere bestellingen hebben het veld niet -> verzending.
  const isPickup = order.customer_details?.deliveryMethod === "pickup";

  const getStatusStyle = (status) => {
    const styles = {
      new: isDark
        ? "bg-indigo-500/15 text-indigo-400"
        : "bg-indigo-50 text-indigo-700",
      handled: isDark
        ? "bg-emerald-500/15 text-emerald-400"
        : "bg-emerald-50 text-emerald-700",
      paid: isDark
        ? "bg-green-500/15 text-green-400"
        : "bg-green-50 text-green-700",
      pending: isDark
        ? "bg-yellow-500/15 text-yellow-400"
        : "bg-yellow-50 text-yellow-700",
      shipped: isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700",
      cancelled: isDark
        ? "bg-red-500/15 text-red-400"
        : "bg-red-50 text-red-700",
    };
    return (
      styles[status] ||
      (isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600")
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: "Nieuwe bestelling",
      handled: "Afgehandeld",
      paid: "Betaald",
      pending: "In afwachting",
      shipped: "Verzonden",
      cancelled: "Geannuleerd",
    };
    return labels[status] || status;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl border p-0 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <div className="flex items-center gap-3">
            <h3
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Bestelling #{order.id}
            </h3>
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${getStatusStyle(order.status)}`}
            >
              {getStatusLabel(order.status)}
            </span>
          </div>
          <span
            className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            {new Date(order.created_at).toLocaleString("nl-BE")}
          </span>
        </div>

        <div className="p-6 space-y-6">
          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-3">
            <div
              className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <h4
                className={`text-[12px] font-medium flex items-center gap-1.5 mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                <Mail className="h-3.5 w-3.5" /> Klantgegevens
              </h4>
              <div className="space-y-1">
                <p
                  className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {order.customer_details?.firstName}{" "}
                  {order.customer_details?.lastName}
                </p>
                <p
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {order.customer_details?.email}
                </p>
                {order.customer_details?.phone && (
                  <p
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {order.customer_details?.phone}
                  </p>
                )}
              </div>
            </div>

            <div
              className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <h4
                className={`text-[12px] font-medium flex items-center gap-1.5 mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                <MapPin className="h-3.5 w-3.5" />{" "}
                {isPickup ? "Afhalen in de winkel" : "Verzendadres"}
              </h4>
              {isPickup ? (
                <div className="space-y-1">
                  <p
                    className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {order.customer_details?.address?.pickup ||
                      "Afhaling in de winkel"}
                  </p>
                  <p
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Geen verzending — klant haalt de bestelling zelf op.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                <p
                  className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  {order.customer_details?.address?.street ?? order.customer_details?.address}{" "}
                  {order.customer_details?.address?.houseNumber}
                </p>
                <p
                  className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  {order.customer_details?.address?.postalCode ?? order.customer_details?.zip}{" "}
                  {order.customer_details?.address?.city ?? order.customer_details?.city}
                </p>
                <p
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {order.customer_details?.address?.country ?? order.customer_details?.country ?? "België"}
                </p>
                </div>
              )}
            </div>

            <div
              className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <h4
                className={`text-[12px] font-medium flex items-center gap-1.5 mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                <CreditCard className="h-3.5 w-3.5" /> Betaling & Status
              </h4>
              <div className="space-y-3">
                <div>
                  <span
                    className={`text-[11px] uppercase tracking-wider block ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Betaalmethode
                  </span>
                  <span
                    className={`text-[13px] font-medium capitalize ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {order.payment_provider || "Onbekend"}
                  </span>
                </div>
                <div>
                  <span
                    className={`text-[11px] uppercase tracking-wider block mb-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Status Wijzigen
                  </span>
                  <select
                    value={order.status}
                    onChange={(e) => onStatusChange(order.id, e.target.value)}
                    className={`h-8 w-full rounded-lg px-2 text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                  >
                    <option value="new">Nieuwe bestelling</option>
                    <option value="handled">Afgehandeld</option>
                    <option value="pending">In afwachting</option>
                    <option value="paid">Betaald</option>
                    <option value="shipped">Verzonden</option>
                    <option value="cancelled">Geannuleerd</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="space-y-3">
            <h4
              className={`text-[13px] font-semibold flex items-center gap-1.5 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              <Package className="h-3.5 w-3.5" /> Bestelde Artikelen
            </h4>
            <div
              className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <Table>
                <TableHeader>
                  <TableRow
                    className={
                      isDark ? "border-white/[0.06]" : "border-gray-100"
                    }
                  >
                    <TableHead
                      className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                    >
                      Product
                    </TableHead>
                    <TableHead
                      className={`text-center text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                    >
                      Aantal
                    </TableHead>
                    <TableHead
                      className={`text-right text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                    >
                      Prijs
                    </TableHead>
                    <TableHead
                      className={`text-right text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                    >
                      Totaal
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items?.map((item, index) => (
                    <TableRow
                      key={index}
                      className={
                        isDark ? "border-white/[0.04]" : "border-gray-50"
                      }
                    >
                      <TableCell
                        className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {item.name}
                      </TableCell>
                      <TableCell
                        className={`text-center text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {item.quantity}
                      </TableCell>
                      <TableCell
                        className={`text-right text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        € {parseFloat(item.price).toFixed(2)}
                      </TableCell>
                      <TableCell
                        className={`text-right text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        € {(item.price * item.quantity).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div
              className={`w-full md:w-1/3 rounded-xl p-4 space-y-2 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <div className="flex justify-between">
                <span
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Subtotaal
                </span>
                <span
                  className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  € {parseFloat(order.total_amount).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Verzending
                </span>
                <span
                  className={`text-[12px] font-medium ${isDark ? "text-green-400" : "text-green-600"}`}
                >
                  Gratis
                </span>
              </div>
              <div
                className={`border-t pt-2 mt-2 ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
              >
                <div className="flex justify-between">
                  <span
                    className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Totaal
                  </span>
                  <span
                    className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    € {parseFloat(order.total_amount).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`flex justify-between pt-4 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <button
              onClick={() => window.print()}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            <button
              onClick={onClose}
              className={`h-8 px-4 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              Sluiten
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetails;
